<template>
  <div>
    <div v-if="errorMessages.length >= 1" class="feedback">
      <transition name="fade" @enter="enterError">
        <FeedbackError v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div v-if="!isApp" class="contact-header">
      <div class="title contact-header__title">
        お問い合わせフォーム
      </div>
    </div>
    <div class="contact-form">
      <form :action="postUrl" method="POST" accept-charset="UTF-8">
        <input name="utf8" type="hidden" value="✓" />
        <input
          type="hidden"
          name="authenticity_token"
          :value="authenticityToken"
        />
        <div class="contact-input__container">
          <div class="contact-input__container--inner">
            <div class="contact__header white-back-container__header">
              <p class="caption require-caption">
                必須*
              </p>
            </div>
            <div
              class="contact__input-container white-back-container__inner-content"
            >
              <div class="contact-type__container contact-form__container">
                <div class="contact-type__label">
                  <span class="require">
                    お問い合わせ種別
                  </span>
                </div>
                <div
                  v-if="contentTypeErrorMessage"
                  class="contact-type__error-message"
                >
                  {{ contentTypeErrorMessage }}
                </div>
                <div class="contact-type__input-container">
                  <select
                    id="contact_form_contact_type"
                    v-model="contactType"
                    name="contact_form[contact_type]"
                    class="select-form"
                    :class="{
                      'select-form--selected': isContactTypeSelected,
                      'select-form--unselected': !isContactTypeSelected,
                      field_with_errors: contentTypeErrorMessage
                    }"
                  >
                    <option value="">
                      選択してください
                    </option>
                    <option
                      v-for="(contentTypeOption, index) in contentTypeOptions"
                      :key="index"
                      :value="contentTypeOption[1]"
                    >
                      {{ contentTypeOption[0] }}
                    </option>
                  </select>
                </div>
              </div>
              <p class="contact-business-link">
                <span class="contact-business-link__description">
                  法人でのご利用、お問い合わせ
                </span>
                <span>
                  は
                  <a
                    href="https://giftee.biz/inquiry/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    こちら
                  </a>
                </span>
              </p>
              <div class="name__container">
                <div class="name__label">
                  <span class="require">
                    お名前
                  </span>
                </div>
                <div
                  v-if="lastNameErrorMessage || firstNameErrorMessage"
                  class="name__error-message"
                >
                  {{ `${lastNameErrorMessage}\n${firstNameErrorMessage}` }}
                </div>
                <div class="name__input-container">
                  <input
                    id="contact_form_last_name"
                    v-model="lastName"
                    type="text"
                    name="contact_form[last_name]"
                    class="input-form name__input--last-name"
                    :class="{
                      'input-form--inputted': isLastNameInputted,
                      'input-form--inputting': isLastNameFocused,
                      field_with_errors: lastNameErrorMessage
                    }"
                    placeholder="姓"
                    @focus="onFocusLastName"
                    @blur="onBlurLastName"
                  />
                  <input
                    id="contact_form_first_name"
                    v-model="firstName"
                    type="text"
                    name="contact_form[first_name]"
                    class="input-form name__input--first-name"
                    :class="{
                      'input-form--inputted': isFirstNameInputted,
                      'input-form--inputting': isFirstNameFocused,
                      field_with_errors: firstNameErrorMessage
                    }"
                    placeholder="名"
                    @focus="onFocusFirstName"
                    @blur="onBlurFirstName"
                  />
                </div>
              </div>
              <div class="name-kana__container">
                <div class="name-kana__label">
                  <span class="require">
                    お名前（ふりがな）
                  </span>
                </div>
                <div
                  v-if="lastNameKanaErrorMessage || firstNameKanaErrorMessage"
                  class="name-kana__error-message"
                >
                  {{
                    `${lastNameKanaErrorMessage}\n${firstNameKanaErrorMessage}`
                  }}
                </div>
                <div class="name-kana__input-container">
                  <input
                    id="contact_form_last_name_kana"
                    v-model="lastNameKana"
                    type="text"
                    name="contact_form[last_name_kana]"
                    class="input-form name-kana__input--last-name"
                    :class="{
                      'input-form--inputted': isLastNameKanaInputted,
                      'input-form--inputting': isLastNameKanaFocused,
                      field_with_errors: lastNameKanaErrorMessage
                    }"
                    placeholder="せい"
                    @focus="onFocusLastNameKana"
                    @blur="onBlurLastNameKana"
                  />
                  <input
                    id="contact_form_first_name_kana"
                    v-model="firstNameKana"
                    type="text"
                    name="contact_form[first_name_kana]"
                    class="input-form name-kana__input--first-name"
                    :class="{
                      'input-form--inputted': isFirstNameKanaInputted,
                      'input-form--inputting': isFirstNameKanaFocused,
                      field_with_errors: firstNameKanaErrorMessage
                    }"
                    placeholder="めい"
                    @focus="onFocusFirstNameKana"
                    @blur="onBlurFirstNameKana"
                  />
                </div>
              </div>
              <div class="email__container">
                <div class="email__label">
                  <span class="require">
                    メールアドレス
                  </span>
                </div>
                <div class="email__minimal-info">
                  メールアドレスを正しく入力いただいていない場合、gifteeからの返信が届きませんのでご注意ください
                </div>
                <div
                  v-if="emailErrorMessage || emailConfirmationErrorMessage"
                  class="email__error-message"
                >
                  {{ `${emailErrorMessage}\n${emailConfirmationErrorMessage}` }}
                </div>
                <div class="email__input-container">
                  <input
                    id="contact_form_email"
                    v-model="email"
                    type="text"
                    name="contact_form[email]"
                    class="input-form email__input"
                    :class="{
                      'input-form--inputted': isEmailInputted,
                      'input-form--inputting': isEmailFocused,
                      field_with_errors: emailErrorMessage
                    }"
                    placeholder="メールアドレス"
                    @focus="onFocusEmail"
                    @blur="onBlurEmail"
                  />
                  <input
                    id="contact_form_email_confirmation"
                    v-model="emailConfirmation"
                    type="text"
                    name="contact_form[email_confirmation]"
                    class="input-form email__input--confirm"
                    :class="{
                      'input-form--inputted': isEmailConfirmationInputted,
                      'input-form--inputting': isEmailConfirmationFocused,
                      field_with_errors: emailConfirmationErrorMessage
                    }"
                    placeholder="メールアドレス（確認）"
                    @focus="onFocusEmailConfirmation"
                    @blur="onBlurEmailConfirmation"
                  />
                </div>
              </div>
              <div class="detail__container">
                <div class="detail__label">
                  <span class="require">
                    お問い合わせ内容
                  </span>
                </div>
                <div class="detail__minimal-info">
                  <p>
                    問い合わせ内容は具体的に入力いただくと、返信がスムーズになります。
                  </p>
                  <p>
                    わかる範囲でご記入をお願いいたします。
                  </p>
                </div>
                <div class="detail__attention">
                  <p>
                    【具体的に記載いただきたいものの参考】
                  </p>
                  <p>
                    ギフトについて：ギフト名、ギフトのURLなど
                  </p>
                  <p>
                    ご購入・受取されたギフトについて：ご注文番号、ギフトURLなど
                  </p>
                  <p>
                    決済について：ご選択された決済方法等
                  </p>
                  <p>
                    キャンペーンについて：対象のキャンペーン名、参加日時など
                  </p>
                  <p>
                    大量のギフト購入について：ご利用目的、ギフト名、個数等
                  </p>
                </div>
                <div v-if="detailErrorMessage" class="detail__error-message">
                  {{ detailErrorMessage }}
                </div>
                <div class="detail__input-container">
                  <textarea
                    id="contact_form_detail"
                    v-model="detail"
                    name="contact_form[detail]"
                    class="input-form"
                    :class="{
                      'input-form--inputted': isDetailInputted,
                      'input-form--inputting': isDetailFocused,
                      field_with_errors: detailErrorMessage
                    }"
                    maxlength="1000"
                    placeholder="お問い合わせ内容をご入力ください"
                    @focus="onFocusDetail"
                    @blur="onBlurDetail"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="submit__container">
          <div class="submit__container--inner">
            <div class="privacy-policy__container">
              <div
                v-if="agreedPrivacyPolicyErrorMessage"
                class="privacy-policy__error-message"
              >
                個人情報の取扱いについての同意が必要です
              </div>
              <div class="privacy-policy__input-container">
                <label
                  class="privacy-policy__label"
                  for="contact_form_agreed_privacy_policy"
                >
                  <div
                    class="privacy-policy__checkbox"
                    :class="{
                      checkbox: !isAgreedPrivacyPolicy,
                      'checkbox--checked': isAgreedPrivacyPolicy
                    }"
                  >
                    <input
                      id="contact_form_agreed_privacy_policy"
                      v-model="isAgreedPrivacyPolicy"
                      type="checkbox"
                      name="contact_form[agreed_privacy_policy]"
                    />
                    <a
                      href="/privacy_policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      @click="onClickPrivacyPolicyLink"
                    >
                      個人情報の取扱いについて
                    </a>
                    に同意する
                  </div>
                </label>
              </div>
            </div>
            <div class="mail-filer-minimal-info">
              ※迷惑メールの対策、キャリアメール等、ドメイン指定を行っている場合、メールが受信できない場合がございます。「{{
                mailDomain
              }}」を受信設定してください。
            </div>
            <div class="business-hour-minimal-info">
              ※メールの対応は平日のみとなっております。週末や祝日にいただいたお問い合わせは、翌営業日（平日）に確認させていただきますのでご了承ください。
            </div>
            <div class="submit-button__container">
              <button
                class="btn btn--primary"
                type="submit"
                :disabled="isSubmitButtonDisabled"
                data-disable-with="送信する"
              >
                送信する
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
    <div v-if="!isApp" class="back-to-top">
      <a href="/" class="back-to-top__link text-link">
        gifteeトップへ戻る
      </a>
    </div>
  </div>
</template>
<script>
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'

export default {
  components: { FeedbackError },
  props: {
    errorMessages: {
      type: Array,
      required: false,
      default: () => []
    },
    isApp: {
      type: Boolean,
      required: true
    },
    postUrl: {
      type: String,
      required: true
    },
    authenticityToken: {
      type: String,
      required: true
    },
    contentTypeErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    contentTypeOptions: {
      type: Array,
      required: false,
      default: () => []
    },
    lastNameErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    firstNameErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    lastNameKanaErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    firstNameKanaErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    emailErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    emailConfirmationErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    detailErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    agreedPrivacyPolicyErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    mailDomain: {
      type: String,
      required: false,
      default: ''
    },
    initialContentType: {
      type: String,
      required: false,
      default: ''
    },
    initialLastName: {
      type: String,
      required: false,
      default: ''
    },
    initialFirstName: {
      type: String,
      required: false,
      default: ''
    },
    initialLastNameKana: {
      type: String,
      required: false,
      default: ''
    },
    initialFirstNameKana: {
      type: String,
      required: false,
      default: ''
    },
    initialEmail: {
      type: String,
      required: false,
      default: ''
    },
    initialEmailConfirmation: {
      type: String,
      required: false,
      default: ''
    },
    initialDetail: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      showableErrorFeedback: false,
      errorMessage: '送信できませんでした',
      lastName: '',
      isLastNameFocused: false,
      firstName: '',
      isFirstNameFocused: false,
      lastNameKana: '',
      isLastNameKanaFocused: false,
      firstNameKana: '',
      isFirstNameKanaFocused: false,
      contactType: '',
      email: '',
      isEmailFocused: false,
      emailConfirmation: '',
      isEmailConfirmationFocused: false,
      detail: '',
      isDetailFocused: false,
      isAgreedPrivacyPolicy: false
    }
  },
  computed: {
    isContactTypeSelected() {
      return this.contactType !== ''
    },
    isLastNameInputted() {
      return this.lastName.length !== 0
    },
    isFirstNameInputted() {
      return this.firstName.length !== 0
    },
    isLastNameKanaInputted() {
      return this.lastNameKana.length !== 0
    },
    isFirstNameKanaInputted() {
      return this.firstNameKana.length !== 0
    },
    isEmailInputted() {
      return this.email.length !== 0
    },
    isEmailConfirmationInputted() {
      return this.emailConfirmation.length !== 0
    },
    isDetailInputted() {
      return this.detail.length !== 0
    },
    isSubmitButtonAvailable() {
      return (
        this.isContactTypeSelected &&
        this.isLastNameInputted &&
        this.isFirstNameInputted &&
        this.isLastNameKanaInputted &&
        this.isFirstNameKanaInputted &&
        this.isEmailInputted &&
        this.isEmailConfirmationInputted &&
        this.isDetailInputted &&
        this.isAgreedPrivacyPolicy
      )
    },
    isSubmitButtonDisabled() {
      return !this.isSubmitButtonAvailable
    }
  },
  created() {
    this.lastName = this.initialLastName
    this.firstName = this.initialFirstName
    this.lastNameKana = this.initialLastNameKana
    this.firstNameKana = this.initialFirstNameKana
    this.contactType = this.initialContentType
    this.email = this.initialEmail
    this.emailConfirmation = this.initialEmailConfirmation
    this.detail = this.initialDetail
  },
  mounted() {
    // フィードバックを表示しない場合はslim側で制御がかかっているので、jsでは常にフィードバックを表示するような形にしている。
    // 最初からshowableErrorFeedbackをtrueにするとenterが発火しないので、ここで変更する
    this.showErrorFeedback()
  },
  methods: {
    // Error Feedback
    showErrorFeedback() {
      this.showableErrorFeedback = true
    },
    enterError() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    // Focus
    onFocusLastName() {
      this.isLastNameFocused = true
    },
    onFocusFirstName() {
      this.isFirstNameFocused = true
    },
    onFocusLastNameKana() {
      this.isLastNameKanaFocused = true
    },
    onFocusFirstNameKana() {
      this.isFirstNameKanaFocused = true
    },
    onFocusEmail() {
      this.isEmailFocused = true
    },
    onFocusEmailConfirmation() {
      this.isEmailConfirmationFocused = true
    },
    onFocusDetail() {
      this.isDetailFocused = true
    },
    // Blur
    onBlurLastName() {
      this.isLastNameFocused = false
    },
    onBlurFirstName() {
      this.isFirstNameFocused = false
    },
    onBlurLastNameKana() {
      this.isLastNameKanaFocused = false
    },
    onBlurFirstNameKana() {
      this.isFirstNameKanaFocused = false
    },
    onBlurEmail() {
      this.isEmailFocused = false
    },
    onBlurEmailConfirmation() {
      this.isEmailConfirmationFocused = false
    },
    onBlurDetail() {
      this.isDetailFocused = false
    },
    onClickPrivacyPolicyLink() {
      event.stopPropagation()
    }
  }
}
</script>
